/* BIG LEDUP 3D-Konfigurator — Shopify theme asset.
   Identisch zur Landingpage-Version, nur: three.js über esm.sh (löst interne Bare-Imports auf, kein importmap nötig)
   und der TV-Pfad kommt aus dem Attribut tv-src (asset_url aus Liquid). */
import * as THREE from 'https://esm.sh/three@0.184.0';
import { OrbitControls } from 'https://esm.sh/three@0.184.0/examples/jsm/controls/OrbitControls.js';

const H = 2.48;        // Rahmenhöhe 248 cm
const PD = 0.12;       // Profiltiefe 12 cm — Eck-/Seitenrahmen sind darum 12 cm kürzer
const BAR = 0.0075;    // Profildicke 7,5 mm — Keder-System, Druck geht randlos in den Rahmen
const FOOT_T = 0.024;  // Fußdicke 2,4 cm
const FOOT_W = 0.10;   // Fußbreite 10 cm
const FOOT_FREE = 0.50;// beidseitiger Fuß 50 cm
const FOOT_IN = 0.31;  // einseitiger Fuß 31 cm, bündig mit der Rückseite
const CH = 0.99;       // Thekenhöhe 99 cm
const CW = 1.00;       // Thekenbreite 100 cm
const C1D = 0.40;      // Counter One: Fuß- und Plattentiefe 40 cm
const CBD = 0.44;      // Counter: Korpustiefe 44 cm
const KAB = 1.00;      // Kabine: 1 × 1 m
const BR_D = 2.00;     // Brücke: 2 m Ausleger
const BR_W = 1.00;     // Brücke: 1 m breit
const TV_W = 1.15;     // TV-Breite
const TV_Y = 1.50;     // Bildmitte auf Augenhöhe
const SILVER = 0xd8dce2;

function glowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const g = c.getContext('2d');
  const rg = g.createRadialGradient(128, 128, 4, 128, 128, 126);
  rg.addColorStop(0, 'rgba(255,255,255,.85)');
  rg.addColorStop(0.45, 'rgba(220,236,255,.28)');
  rg.addColorStop(1, 'rgba(220,236,255,0)');
  g.fillStyle = rg;
  g.fillRect(0, 0, 256, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

class Ledup3D extends HTMLElement {
  static get observedAttributes() { return ['typ', 'breite', 'licht', 'image', 'addons']; }

  connectedCallback() {
    if (this._ready) return;
    this.style.display = 'block';
    this.style.position = 'absolute';
    this.style.inset = '0';
    try { this.init(); } catch (e) { this.fallback(); return; }
    this._ready = true;
    this.build();
  }

  disconnectedCallback() {
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this._ro) this._ro.disconnect();
    if (this.renderer) this.renderer.dispose();
  }

  attributeChangedCallback() { if (this._ready) this.build(); }

  fallback() {
    this.innerHTML = '<div style="position:absolute;inset:0;display:grid;place-items:center;color:#9aa4b2;font:14px Poppins,sans-serif;text-align:center;padding:20px">3D-Vorschau benötigt WebGL.<br>Bitte Browser aktualisieren.</div>';
  }

  init() {
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x12161b, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.domElement.style.cssText = 'display:block;width:100%;height:100%;cursor:grab;touch-action:pan-y';
    this.appendChild(renderer.domElement);
    this.renderer = renderer;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x12161b, 12, 34);
    this.scene = scene;

    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 120);
    camera.position.set(2.6, 1.9, 6.4);
    this.camera = camera;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = 2.4;
    controls.maxDistance = 16;
    controls.minPolarAngle = Math.PI * 0.16;
    controls.maxPolarAngle = Math.PI * 0.495;
    controls.target.set(0, 1.2, 0);
    controls.touches.ONE = THREE.TOUCH.NONE;
    controls.touches.TWO = THREE.TOUCH.DOLLY_ROTATE;
    controls.addEventListener('start', () => { this._touched = true; });
    this.controls = controls;
    renderer.domElement.style.touchAction = 'pan-y';

    scene.add(new THREE.HemisphereLight(0xa8bdd6, 0x0b0e12, 0.7));
    const fill = new THREE.DirectionalLight(0xc9dcff, 0.55);
    fill.position.set(0.5, 2.4, 8);
    scene.add(fill);
    const key = new THREE.DirectionalLight(0xffffff, 1.15);
    key.position.set(4.5, 6.5, 5.5);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = -9; key.shadow.camera.right = 9;
    key.shadow.camera.top = 9; key.shadow.camera.bottom = -9;
    key.shadow.bias = -0.0012;
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x8fb6ff, 0.35);
    rim.position.set(-6, 3, -4);
    scene.add(rim);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(60, 60),
      new THREE.MeshStandardMaterial({ color: 0x1a1f26, roughness: 0.72, metalness: 0.08 })
    );
    floor.name = 'boden';
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    const grid = new THREE.GridHelper(48, 48, 0x38424f, 0x232b34);
    grid.name = 'raster_1m';
    grid.position.y = 0.004;
    grid.material.transparent = true;
    grid.material.opacity = 0.5;
    scene.add(grid);

    this.glowTex = glowTexture();
    this.loader = new THREE.TextureLoader();
    this.silver = new THREE.MeshStandardMaterial({ name: 'aluminium', color: SILVER, metalness: 0.5, roughness: 0.26 });
    this.dark = new THREE.MeshStandardMaterial({ name: 'rueckwand', color: 0x23282f, roughness: 0.9, metalness: 0.05 });
    this.top = new THREE.MeshStandardMaterial({ name: 'thekenplatte', color: 0x1c2126, roughness: 0.42, metalness: 0.12 });
    this.alu = new THREE.MeshStandardMaterial({ name: 'blende', color: 0xb9c1cb, roughness: 0.55, metalness: 0.25 });
    this.wood = new THREE.MeshStandardMaterial({ name: 'holz_schwarz', color: 0x14171b, roughness: 0.6, metalness: 0.03 });

    this.root = new THREE.Group();
    scene.add(this.root);

    this._ro = new ResizeObserver(() => this.resize());
    this._ro.observe(this);
    this.resize();

    const tick = () => {
      this._raf = requestAnimationFrame(tick);
      controls.update();
      renderer.render(scene, camera);
    };
    tick();
  }

  resize() {
    const w = this.clientWidth || 640, h = this.clientHeight || 400;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this._frame && !this._touched) this._frame();
  }

  texFor(src, repeat, offset) {
    const t = this.loader.load(src);
    t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.repeat.set(repeat, 1);
    t.offset.set(offset, 0);
    t.anisotropy = 4;
    return t;
  }

  // Motiv formatfüllend auf eine Fläche mit dem Seitenverhältnis a legen
  coverTex(src, a) {
    const t = this.loader.load(src, () => {
      const img = t.image;
      if (!img || !img.width) return;
      const ia = img.width / img.height;
      if (ia > a) { const r = a / ia; t.repeat.set(r, 1); t.offset.set((1 - r) / 2, 0); }
      else { const r = ia / a; t.repeat.set(1, r); t.offset.set(0, (1 - r) / 2); }
      t.needsUpdate = true;
    });
    t.colorSpace = THREE.SRGBColorSpace;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.anisotropy = 4;
    return t;
  }

  // leuchtende Textilfläche
  litMat(image, licht, tex) {
    const mat = new THREE.MeshStandardMaterial({
      name: 'textildruck', roughness: 0.92, metalness: 0,
      color: image ? 0xffffff : 0x2c323a,
      emissive: image ? 0xffffff : 0x141922,
      emissiveIntensity: licht ? 1.0 : 0.04,
      side: THREE.FrontSide
    });
    mat.userData.own = true;
    if (image && tex) { mat.map = tex; mat.emissiveMap = tex; }
    return mat;
  }

  bar(w, h, d, x, y, z, name) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), this.silver);
    m.name = name || 'profil';
    m.position.set(x, y, z);
    m.castShadow = true;
    return m;
  }

  /* Ein Leuchtrahmen: Grafik auf z≈0 nach +Z, Korpus bis z=-pd.
     o: { w, h, pd, image, repeat, offset, cover, licht, feet:'free'|'in'|'back'|'none', footLen, pool, back } */
  frame(o) {
    const w = o.w, h = o.h || H, pd = o.pd || PD, licht = o.licht;
    const oe = o.openEdge || 0;   // 1/-1: eine Seite ohne Profil, 2: beide — Druck läuft bis zur Kante
    const both = oe === 2;
    const g = new THREE.Group();
    const bars = [
      [w, BAR, pd, 0, h - BAR / 2, -pd / 2],
      [w, BAR, pd, 0, BAR / 2, -pd / 2]
    ];
    if (!both && oe !== -1) bars.push([BAR, h - BAR * 2, pd, -w / 2 + BAR / 2, h / 2, -pd / 2]);
    if (!both && oe !== 1) bars.push([BAR, h - BAR * 2, pd, w / 2 - BAR / 2, h / 2, -pd / 2]);
    bars.forEach((b, i) => g.add(this.bar(b[0], b[1], b[2], b[3], b[4], b[5], 'profil_' + i)));

    if (o.back !== false) {
      const back = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.006), this.dark);
      back.name = 'rueckseite';
      back.position.set(0, h / 2, -pd + 0.004);
      g.add(back);
    }

    const feet = o.feet || 'none';
    if (feet !== 'none') {
      const len = o.footLen || (feet === 'free' ? FOOT_FREE : FOOT_IN);
      const cz = feet === 'free' ? -pd / 2 : feet === 'back' ? -len / 2 : -pd + len / 2;
      [-1, 1].forEach((s, i) => {
        const foot = new THREE.Mesh(new THREE.BoxGeometry(FOOT_W, FOOT_T, len), this.silver);
        foot.name = 'fuss_' + i;
        foot.position.set(s * Math.max(FOOT_W / 2, w / 2 - FOOT_W / 2 - 0.01), FOOT_T / 2, cz);
        foot.castShadow = true;
        g.add(foot);
      });
    }

    const fw = w - (both ? 0 : BAR * (oe ? 1 : 2)), fh = h - BAR * 2;
    const cx = both ? 0 : oe * BAR / 2;
    let tex = null;
    if (o.image) tex = o.cover ? this.coverTex(o.image, fw / fh) : this.texFor(o.image, o.repeat, o.offset);
    const mat = this.litMat(o.image, licht, tex);
    const face = new THREE.Mesh(new THREE.PlaneGeometry(fw, fh), mat);
    face.name = 'grafik';
    face.position.set(cx, h / 2, -0.0015);
    g.add(face);

    if (licht) {
      const halo = new THREE.Mesh(
        new THREE.PlaneGeometry(fw, fh),
        new THREE.MeshBasicMaterial({
          map: mat.map || null, color: mat.map ? 0xffffff : 0x9dc4ff,
          transparent: true, opacity: mat.map ? 0.1 : 0.05,
          blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false
        })
      );
      halo.material.userData.own = true;
      halo.name = 'lichtsaum';
      halo.position.set(cx, h / 2, 0.003);
      g.add(halo);

      if (o.pool) {
        const pool = new THREE.Mesh(
          new THREE.PlaneGeometry(w * 1.5, 3.2),
          new THREE.MeshBasicMaterial({
            map: this.glowTex, transparent: true, opacity: 0.42,
            blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false
          })
        );
        pool.material.userData.own = true;
        pool.name = 'lichtkegel';
        pool.rotation.x = -Math.PI / 2;
        pool.position.set(0, 0.012, 0.9);
        g.add(pool);
      }
    }
    return g;
  }

  /* Counter: ein Leuchtrahmen 100 × 99 cm, vorn und hinten bedruckt.
     Der Rahmen sitzt mittig auf den 40-cm-Füßen, die Platte ragt vorn und hinten über. */
  counterSimple(image, licht) {
    const g = new THREE.Group();
    g.name = 'counter';
    const body = this.frame({ w: CW, h: CH, image, cover: true, licht, back: false });
    body.position.z = PD / 2;
    g.add(body);

    const bw = CW - BAR * 2, bh = CH - BAR * 2;
    const rear = new THREE.Mesh(
      new THREE.PlaneGeometry(bw, bh),
      this.litMat(image, licht, image ? this.coverTex(image, bw / bh) : null)
    );
    rear.name = 'grafik_rueckseite';
    rear.rotation.y = Math.PI;
    rear.position.set(0, CH / 2, -PD / 2 + 0.0015);
    g.add(rear);

    [-1, 1].forEach((s, i) => {
      const foot = new THREE.Mesh(new THREE.BoxGeometry(FOOT_W, FOOT_T, C1D), this.silver);
      foot.name = 'fuss_' + i;
      foot.position.set(s * (CW / 2 - FOOT_W / 2 - 0.01), FOOT_T / 2, 0);
      foot.castShadow = true;
      g.add(foot);
    });

    const plate = new THREE.Mesh(new THREE.BoxGeometry(CW + 0.04, 0.026, C1D), this.top);
    plate.name = 'thekenplatte';
    plate.position.set(0, CH + 0.013, 0);
    plate.castShadow = true;
    g.add(plate);
    return g;
  }

  /* Counter One: geschlossener Korpus 100 × 44 × 99 cm. Front und Seiten bedruckt —
     das Seitentextil läuft ohne Profil bis in die hintere Ecke. Rückseite: außen je ein
     Profil, dazwischen zwei schwarze Holztüren. */
  counterOne(image, licht) {
    const g = new THREE.Group();
    g.name = 'counter_one';
    const d = CBD;

    const front = this.frame({ w: CW, h: CH, image, cover: true, licht, back: false });
    front.position.z = d / 2;
    g.add(front);

    // Seiten: unbeleuchteter Textildruck, hinten profillos bis in die Ecke
    [-1, 1].forEach(s => {
      const side = this.frame({ w: d - BAR, h: CH, image, cover: true, licht: false, back: false, openEdge: s });
      side.rotation.y = s * Math.PI / 2;
      side.position.set(s * CW / 2, 0, -BAR / 2);
      g.add(side);
    });

    const rear = new THREE.Group();
    rear.name = 'rueckseite';
    rear.position.z = -d / 2;
    [-1, 1].forEach((s, i) =>
      rear.add(this.bar(BAR, CH, 0.022, s * (CW / 2 - BAR - 0.006), CH / 2, 0.011, 'profil_' + i)));
    const dw = (CW - BAR * 2) / 2 - 0.005;
    [-1, 1].forEach((s, i) => {
      const door = new THREE.Mesh(new THREE.BoxGeometry(dw, CH - 0.014, 0.018), this.wood);
      door.name = 'holztuer_' + i;
      door.position.set(s * (dw / 2 + 0.005), CH / 2, 0.009);
      door.castShadow = true;
      rear.add(door);
      rear.add(this.bar(0.016, 0.10, 0.02, s * 0.035, CH / 2, -0.012, 'tuergriff_' + i));
    });
    g.add(rear);

    const plate = new THREE.Mesh(new THREE.BoxGeometry(CW + 0.05, 0.03, d + 0.06), this.top);
    plate.name = 'thekenplatte';
    plate.position.set(0, CH + 0.015, 0);
    plate.castShadow = true;
    g.add(plate);
    return g;
  }

  /* Brücke: 1 m breiter Rahmen 2 m vor der Wand, darüber ein 2 × 1 m Ausleger,
     der auf Rahmen und Rückwand aufliegt. Ursprung = Wandvorderkante. */
  bruecke(image, licht) {
    const g = new THREE.Group();
    g.name = 'bruecke';
    const post = this.frame({ w: BR_W, h: H, image, cover: true, licht, feet: 'free' });
    post.position.z = BR_D;
    g.add(post);

    const deck = new THREE.Group();
    deck.name = 'ausleger';
    deck.position.set(0, H, BR_D / 2 - PD / 2);
    const L = BR_D;
    [
      [BR_W, PD, BAR, 0, PD / 2, -L / 2 + BAR / 2],
      [BR_W, PD, BAR, 0, PD / 2, L / 2 - BAR / 2],
      [BAR, PD, L, -BR_W / 2 + BAR / 2, PD / 2, 0],
      [BAR, PD, L, BR_W / 2 - BAR / 2, PD / 2, 0]
    ].forEach((b, i) => deck.add(this.bar(b[0], b[1], b[2], b[3], b[4], b[5], 'ausleger_profil_' + i)));
    const uw = BR_W - BAR * 2, ul = L - BAR * 2;
    const under = new THREE.Mesh(new THREE.PlaneGeometry(uw, ul), this.alu);
    under.name = 'ausleger_unterseite';
    under.rotation.x = Math.PI / 2;
    under.position.y = 0.004;
    deck.add(under);
    const tex = image ? this.coverTex(image, uw / ul) : null;
    const mat = this.litMat(image, licht, tex);
    const face = new THREE.Mesh(new THREE.PlaneGeometry(uw, ul), mat);
    face.name = 'ausleger_grafik';
    face.rotation.x = -Math.PI / 2;
    face.position.y = PD - 0.0015;
    deck.add(face);
    g.add(deck);
    return g;
  }

  /* Kabine: zwei Rahmen à 1 m schließen eine Ecke zu 1 m² Abstellraum.
     Ursprung = Ecke (Innenflucht Seitenwand × Vorderkante Rückwand), öffnet nach +X/+Z. */
  kabine(image, licht, depth) {
    const g = new THREE.Group();
    g.name = 'kabine';
    const d = depth || KAB;
    const dz = d;                   // Türebene an der Kabinenkante
    const wx = KAB + PD / 2;        // Eckrahmen liegt seitlich direkt an der Tür an
    // Tür: Textildruck, unbeleuchtet. Ihre Seitenprofile sind nur seitlich sichtbar —
    // rechts wird sie vom davorstehenden Eckrahmen verdeckt.
    const a = this.frame({ w: KAB, h: H, image, cover: true, licht: false });
    a.name = 'kabine_tuer';
    a.position.set(KAB / 2, 0, dz);
    g.add(a);
    // Eckrahmen: exakt 1 m, 90° zur Tür, greift mit seiner Profiltiefe vor die Türkante
    const b = this.frame({ w: KAB, h: H, image, cover: true, licht, openEdge: 1 });
    b.name = 'kabine_wand';
    b.rotation.y = Math.PI / 2;
    b.position.set(wx, 0, KAB / 2);   // Hinterkante liegt an der Rückwand an
    g.add(b);

    // Schiene liegt flach über der Tür auf Seitenwand und Eckrahmen auf
    const rail = this.bar(wx + 0.02, BAR, PD, wx / 2, H + BAR / 2, dz - PD / 2, 'schiene');
    g.add(rail);

    const grip = new THREE.Group();
    grip.name = 'griff';
    grip.add(this.bar(0.028, 0.20, 0.026, 0, 0, 0, 'griff_bar'));
    grip.add(this.bar(0.022, 0.026, 0.05, 0, 0.088, -0.028, 'griff_arm_o'));
    grip.add(this.bar(0.022, 0.026, 0.05, 0, -0.088, -0.028, 'griff_arm_u'));
    grip.position.set(KAB - 0.13, 1.06, dz + 0.045);
    g.add(grip);
    return g;
  }

  detachTv() { if (this._tv && this._tv.parent) this._tv.parent.remove(this._tv); }

  loadTv() {
    if (this._tv || this._tvLoading || this._tvFail) return;
    this._tvLoading = true;
    import('https://esm.sh/three@0.184.0/examples/jsm/loaders/GLTFLoader.js').then(({ GLTFLoader }) => {
      new GLTFLoader().load(this.getAttribute('tv-src') || 'assets/tv.glb', gltf => {
        const s = gltf.scene;
        s.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(s);
        const size = box.getSize(new THREE.Vector3());
        const c = box.getCenter(new THREE.Vector3());
        s.position.sub(c);
        s.traverse(m => {
          if (!m.isMesh) return;
          m.castShadow = true;
          const mat = m.material;
          if (mat && /screen|glass/i.test(mat.name || '')) {
            mat.emissive = new THREE.Color(0x0a1420);
            mat.emissiveIntensity = 1;
          }
        });
        const inner = new THREE.Group();
        inner.add(s);
        inner.rotation.y = Math.PI;                       // Bildschirm nach +Z
        inner.userData.unitScale = 1 / Math.max(0.001, size.x);
        inner.scale.setScalar(TV_W * inner.userData.unitScale);
        const holder = new THREE.Group();
        holder.name = 'tv_halter';
        holder.userData.keep = true;
        holder.add(inner);
        this._tv = holder;
        this._tvLoading = false;
        this.build();
      }, undefined, () => { this._tvFail = true; this._tvLoading = false; });
    }).catch(() => { this._tvFail = true; this._tvLoading = false; });
  }

  // TV nie breiter als die Trägerfläche — die Streben sitzen im Rahmen
  tvWidth(w) {
    const inner = this._tv && this._tv.children[0];
    if (!inner || !inner.userData.unitScale) return;
    inner.scale.setScalar(w * inner.userData.unitScale);
  }  disposeRoot() {
    while (this.root.children.length) {
      const c = this.root.children.pop();
      c.traverse(o => {
        if (o.userData && o.userData.keep) return;
        if (o.geometry) o.geometry.dispose();
        const m = o.material;
        if (m && m.userData && m.userData.own) {
          if (m.map && m.map !== this.glowTex) m.map.dispose();
          m.dispose();
        }
      });
    }
  }

  build() {
    const typ = this.getAttribute('typ') || 'kopf';
    const B = Math.max(1, parseFloat(this.getAttribute('breite') || '3'));
    const licht = this.getAttribute('licht') !== 'off';
    const image = this.getAttribute('image') || '';
    const ad = (this.getAttribute('addons') || '').split(/[\s,]+/).filter(Boolean);
    const has = n => ad.indexOf(n) >= 0;
    const wantKab = has('kabine') && typ !== 'kopf' && B >= 3;
    const wantBr = has('bruecke');
    const wantTv = has('tv');
    const theke = has('counter-one') ? 'one' : has('counter') ? 'simple' : null;

    this.detachTv();
    this.disposeRoot();

    const side = typ === 'eck' ? 3 : typ === 'reihe' ? 1 : 0;
    // Reihenstand: Seitenwände volle 1 m, damit sie zur 1-m-Kabinenwand passen
    const sideW = typ === 'reihe' ? side : Math.max(0.4, side - PD);
    const walls = [];
    if (typ === 'eck') walls.push({ w: sideW, dir: 'left' });
    if (typ === 'reihe') walls.push({ w: sideW, dir: 'left' });
    walls.push({ w: B, dir: 'front' });
    if (typ === 'reihe') walls.push({ w: sideW, dir: 'right' });

    const totalM = walls.reduce((a, w) => a + w.w, 0);
    const freeStanding = walls.length === 1;
    let before = 0;
    walls.forEach(wall => {
      const g = this.frame({
        w: wall.w, image, repeat: wall.w / totalM, offset: before / totalM,
        licht, feet: freeStanding ? 'free' : 'in', pool: true
      });
      before += wall.w;
      if (wall.dir === 'left') {
        g.rotation.y = Math.PI / 2;
        g.position.set(-B / 2 + PD, 0, wall.w / 2);
      } else if (wall.dir === 'right') {
        g.rotation.y = -Math.PI / 2;
        g.position.set(B / 2 - PD, 0, wall.w / 2);
      }
      g.name = 'rahmen_' + wall.dir;
      this.root.add(g);
    });

    const xi = -B / 2 + PD;              // Innenflucht der linken Seitenwand
    const kabD = Math.min(KAB, typ === 'reihe' ? sideW : KAB);
    const kabEnd = wantKab ? xi + KAB : xi;

    if (wantKab) {
      const k = this.kabine(image, licht, kabD);
      k.position.set(xi, 0, 0);
      this.root.add(k);
    }

    // Brücke: an der Rückwand, beim Eckstand an der längeren Wand, nie direkt am Rand
    let brGroup = null;
    if (wantBr) {
      brGroup = this.bruecke(image, licht);
      // liegt am rechten Ende der Rückwand auf und reicht 1 m in den Stand
      let cx = B / 2 - PD - BR_W / 2;
      cx = Math.max(cx, kabEnd + 0.55 + BR_W / 2);
      brGroup.position.set(cx, 0, 0);
      if (typ === 'reihe') {
        let rx = B >= 4 ? B / 2 - 1.6 : 0;
        rx = Math.max(rx, kabEnd + 0.55 + BR_W / 2);
        brGroup.position.x = Math.min(rx, B / 2 - 0.5 - BR_W / 2);
      }
      this.root.add(brGroup);
    }

    if (theke) {
      const c = theke === 'one' ? this.counterOne(image, licht) : this.counterSimple(image, licht);
      const inner = typ === 'kopf' ? -(B / 2 - 0.72) : B / 2 - PD - 0.66;
      const lo = Math.max(kabEnd + 0.65, -B / 2 + 0.55), hi = B / 2 - 0.55;
      let cx = Math.min(Math.max(inner, lo), Math.max(lo, hi));
      const cz = 1.35;
      // möglichst neben der Brücke — nie in den Gang hinaus, immer parallel zur Rückwand
      if (brGroup) {
        const bx = brGroup.position.x, need = 1.05;
        if (Math.abs(cx - bx) < need) {
          const left = bx - need, right = bx + need;
          if (left >= lo) cx = left;
          else if (right <= hi + 0.12) cx = Math.min(right, hi + 0.05);
          else cx = Math.min(Math.max(bx, lo), Math.max(lo, hi));
        }
      }
      c.position.set(cx, 0, cz);
      this.root.add(c);
    }

    if (wantTv) {
      if (!this._tv) {
        this.loadTv();
      } else if (brGroup) {
        // hängt vorn am Brückenrahmen, auf Augenhöhe — Streben innerhalb des 1-m-Rahmens
        this.tvWidth(BR_W - 0.16);
        this._tv.position.set(0, TV_Y, BR_D + 0.035);
        brGroup.add(this._tv);
      } else {
        let tvX = 0;
        if (wantKab) {
          const lo = kabEnd + TV_W / 2 + 0.15, hi = B / 2 - TV_W / 2 - 0.15;
          tvX = hi > lo ? (lo + hi) / 2 : Math.max(lo, hi);
        }
        this.tvWidth(Math.min(TV_W, B - 0.3));
        this._tv.position.set(tvX, TV_Y, 0.035);
        this.root.add(this._tv);
      }
    }

    this._frame = () => {
      const aspect = Math.max(0.6, (this.clientWidth || 640) / (this.clientHeight || 400));
      const vHalf = (this.camera.fov * Math.PI / 180) / 2;
      const hHalf = Math.atan(Math.tan(vHalf) * aspect);
      const box = new THREE.Box3().setFromObject(this.root);
      const c = box.getCenter(new THREE.Vector3());
      const az = typ === 'eck' ? 0.5 : typ === 'reihe' ? 0.34 : 0.24;
      const target = new THREE.Vector3(c.x, Math.min(1.35, c.y), c.z);
      const dir = new THREE.Vector3(Math.sin(az), 0, Math.cos(az));
      const corners = [];
      [box.min.x, box.max.x].forEach(x => [box.min.y, box.max.y].forEach(y =>
        [box.min.z, box.max.z].forEach(z => corners.push(new THREE.Vector3(x, y, z)))));
      const up0 = new THREE.Vector3(0, 1, 0);
      const camAt = d => new THREE.Vector3(target.x + dir.x * d, Math.min(1.75, 1.45 + d * 0.02), target.z + dir.z * d);
      let d = 6;
      for (let i = 0; i < 20; i++) {
        const cam = camAt(d);
        const fwd = target.clone().sub(cam).normalize();
        const right = new THREE.Vector3().crossVectors(fwd, up0).normalize();
        const up = new THREE.Vector3().crossVectors(right, fwd);
        let need = 0;
        corners.forEach(p => {
          const v = p.clone().sub(cam);
          const z = v.dot(fwd);
          const kx = Math.abs(v.dot(right)) / Math.tan(hHalf) - z;
          const ky = Math.abs(v.dot(up)) / Math.tan(vHalf) - z;
          need = Math.max(need, d + Math.max(kx, ky));
        });
        const nd = d + (need - d) * 0.8;
        if (Math.abs(nd - d) < 0.02) { d = nd; break; }
        d = nd;
      }
      d = Math.min(Math.max(d * 1.05, 2.6), 26);
      this.controls.target.copy(target);
      this.camera.position.copy(camAt(d));
      this.controls.update();
    };
    if (!this._touched) this._frame();
  }
}

if (!customElements.get('ledup-3d')) customElements.define('ledup-3d', Ledup3D);
